import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api.ts'
import { plural } from '../format.ts'
import { NewClientDialog } from '../components/NewClientDialog.tsx'

interface ClientRow {
  id: string
  name: string
  type: string
  nip: string | null
  city: string | null
  phone: string | null
  email: string | null
  status: string
  ownerCode: string | null
}

const STATUS_STYLE: Record<string, string> = {
  aktywny: 'bg-green-100 text-green-700',
  potencjalny: 'bg-amber-100 text-amber-700',
  nieaktywny: 'bg-slate-100 text-slate-500',
}

export function ClientsPage() {
  const [clients, setClients] = useState<ClientRow[]>([])
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function reload() {
    const params = new URLSearchParams()
    if (search.trim()) params.set('search', search.trim())
    if (status) params.set('status', status)
    setLoading(true)
    api
      .get<{ clients: ClientRow[] }>(`/api/clients?${params}`)
      .then((r) => {
        setClients(r.clients)
        setError(null)
      })
      // Swallowing this showed "Brak klientów" when the network died — telling
      // the user their database is empty when it isn't.
      .catch((e) => setError(e instanceof Error ? e.message : 'Nie udało się pobrać listy'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const t = setTimeout(reload, 150)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status])

  return (
    <div className="p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-800">Klienci</h1>
        <button
          onClick={() => setAdding(true)}
          className="text-sm bg-brand-600 text-white px-3 py-1.5 rounded-md hover:bg-brand-700"
        >
          Dodaj klienta
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <input
          type="search"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Szukaj: nazwa, NIP, miasto, telefon…"
          className="flex-1 min-w-64 px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:border-brand-600"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
        >
          <option value="">Wszystkie statusy</option>
          <option value="aktywny">Aktywny</option>
          <option value="potencjalny">Potencjalny</option>
          <option value="nieaktywny">Nieaktywny</option>
        </select>
      </div>

      {error && (
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3">
          {error}
        </div>
      )}

      <div className="text-xs text-slate-500 mb-2">
        {loading ? 'Szukam…' : `${clients.length} ${plural(clients.length, 'klient', 'klientów', 'klientów')}`}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase text-slate-500">
              <th className="px-3 py-2 font-medium">Nazwa</th>
              <th className="px-3 py-2 font-medium">Miasto</th>
              <th className="px-3 py-2 font-medium">NIP</th>
              <th className="px-3 py-2 font-medium">Telefon</th>
              <th className="px-3 py-2 font-medium">Opiekun</th>
              <th className="px-3 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-brand-50/50">
                <td className="px-3 py-1.5">
                  <Link to={`/klienci/${c.id}`} className="text-brand-700 hover:underline font-medium">
                    {c.name}
                  </Link>
                </td>
                <td className="px-3 py-1.5 text-slate-600">{c.city ?? '—'}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-slate-500">{c.nip ?? '—'}</td>
                <td className="px-3 py-1.5 text-slate-600">{c.phone ?? '—'}</td>
                <td className="px-3 py-1.5 text-xs font-mono text-slate-500">{c.ownerCode ?? '—'}</td>
                <td className="px-3 py-1.5">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_STYLE[c.status] ?? ''}`}>{c.status}</span>
                </td>
              </tr>
            ))}
            {!loading && clients.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-slate-400">
                  {search || status ? 'Nic nie znaleziono.' : 'Brak klientów — dodaj pierwszego.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {adding && (
        <NewClientDialog
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false)
            reload()
          }}
        />
      )}
    </div>
  )
}
